import React from "react";
import { Badge, Button, Group, Stack } from "@mantine/core";
import { IconEdit } from "@tabler/icons-react";
import { SettingsField } from "../common";
import BusinessHoursForm from "../../forms/BusinessHoursForm";

const DAYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const;

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export interface BusinessHoursTabProps {
  businessInfo: any;
  editing: boolean;
  /** A save is in flight — shows the Save button's loading state. */
  saving?: boolean;
  readOnly?: boolean;
  onEdit: () => void;
  onCancel: () => void;
  /** Persist a partial (hours-only) salon payload. */
  onSave: (payload: Record<string, any> | null) => void | Promise<void>;
}

export default function BusinessHoursTab({
  businessInfo,
  editing,
  saving = false,
  readOnly = false,
  onEdit,
  onCancel,
  onSave,
}: BusinessHoursTabProps) {
  if (editing) {
    return (
      <Stack gap={0}>
        <Group justify="flex-end" mb="sm">
          <Button
            variant="subtle"
            color="gray"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </Button>
        </Group>
        <BusinessHoursForm
          initialData={businessInfo}
          onSubmit={onSave}
          onClose={onCancel}
          buttonText="Save Hours"
          saving={saving}
        />
      </Stack>
    );
  }

  return (
    <Stack gap={0}>
      {!readOnly && (
        <Group justify="flex-end" mb="sm">
          <Button
            variant="default"
            leftSection={<IconEdit size={16} />}
            onClick={onEdit}
          >
            Edit
          </Button>
        </Group>
      )}

      {DAYS.map((day, index) => {
        const open = businessInfo?.[`${day}_open_time`];
        const end = businessInfo?.[`${day}_end_time`];
        return (
          <SettingsField
            key={day}
            label={capitalize(day)}
            withDivider={index < DAYS.length - 1}
          >
            {open ? (
              <Group gap="sm" justify="flex-end" wrap="nowrap">
                {open} - {end}
                <Badge color="green" variant="light">
                  Open
                </Badge>
              </Group>
            ) : (
              <Badge color="red" variant="light">
                Closed
              </Badge>
            )}
          </SettingsField>
        );
      })}
    </Stack>
  );
}
